/** Port von `Client/Pages/Views/IwmViewContainer.razor`. */

import { containerApi } from '@/api/services/containerApi';
import { containerCellTemplates } from '@/components/columns/containerColumns';
import { containerEditTemplates } from '@/components/columns/edit/containerEditColumns';
import { ContainerCloneDialog } from '@/components/dialogs/ContainerCloneDialog';
import { ContainerDocumentListDialog } from '@/components/dialogs/ContainerDocumentListDialog';
import { ContainerDocumentUploadDialog } from '@/components/dialogs/ContainerDocumentUploadDialog';
import { DataGrid } from '@/components/grid/DataGrid';
import { ContainerSidebar } from '@/components/sidebars/ContainerSidebar';
import { useSidebar } from '@/components/sidebars/useSidebar';
import { Button } from '@/components/ui';
import { AuditButton } from '@/components/views/AuditButton';
import { DeleteButton } from '@/components/views/DeleteButton';
import { DocumentsDropDown } from '@/components/views/DocumentsDropDown';
import { ExcelExportButton } from '@/components/views/ExcelExportButton';
import { MessageDialog } from '@/components/views/MessageDialog';
import { NewButton } from '@/components/views/NewButton';
import { ViewAuditSidebar } from '@/components/views/ViewAuditSidebar';
import { ViewPageFrame } from '@/components/views/ViewPageFrame';
import { firstSelected } from '@/components/views/firstSelected';
import { useMessageDialog } from '@/components/views/useMessageDialog';
import { useViewIdParam } from '@/components/views/useViewIdParam';
import { useViewPage } from '@/hooks/useViewPage';
import type { Container } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

export function ContainerView() {
  const viewId = useViewIdParam();
  const page = useViewPage<Container>({
    viewId,
    entity: EntityTypes.Container,
    api: containerApi,
    cellTemplates: containerCellTemplates,
    editTemplates: containerEditTemplates,
  });
  const { permissions, selectedId, view } = page;
  const message = useMessageDialog();
  const refreshGrid = (): void => void page.gridRef.current?.refresh();

  const auditSidebar = useSidebar();
  const containerSidebar = useSidebar({ onHide: refreshGrid });
  // `IwmBaseDialog` und `IwmBaseSidebar` teilen den Vertrag `Visible`/`Show()`/`Hide()`;
  // die Sichtbarkeit der Dialoge hängt deshalb am selben Regler.
  const cloneDialog = useSidebar();
  const uploadDialog = useSidebar();
  const listDialog = useSidebar();

  // Port von `Recalc()`.
  const recalc = async (): Promise<void> => {
    try {
      const model = firstSelected(page.gridRef.current?.getSelectedRecords() ?? []);
      const result = await containerApi.reCalc(viewId, model.Id);
      page.gridRef.current?.setRowData(result.Id, result);
      message.show('Calculation', 'Freight charges calculated successfully!');
    } catch (error) {
      message.show('Freight charge calculation failed.', String(error));
    }
  };

  // Port von `Clone()`. Bei leerer Auswahl bleibt der Zugriff auf `recs.First()` aus; der
  // sichtbare Effekt ist derselbe, weil die Meldung dann bereits gesetzt ist.
  const clone = (): void => {
    const records = page.gridRef.current?.getSelectedRecords() ?? [];
    if (records.length !== 1) {
      message.show('Duplicate Container', 'Please select an entry.');
      return;
    }
    cloneDialog.show();
  };

  const toolbar = (
    <>
      {permissions?.AllowCreate === true ? (
        <NewButton
          onClick={() => {
            containerSidebar.show();
          }}
        />
      ) : null}
      {permissions?.AllowAudit === true ? (
        <AuditButton
          selectedId={selectedId}
          onClick={() => {
            auditSidebar.show();
          }}
        />
      ) : null}
      {view?.AdditionalFunctions?.includes('Dokument') === true ? (
        <DocumentsDropDown
          selectedId={selectedId}
          onUpload={() => {
            uploadDialog.show();
          }}
          onList={() => {
            listDialog.show();
          }}
        />
      ) : null}
      {view?.AdditionalFunctions?.includes('Frachtkosten') === true ? (
        <Button className="button-c" disabled={selectedId === null} onClick={() => recalc()}>
          Calculate Freight Charges
        </Button>
      ) : null}
      {view?.AdditionalFunctions?.includes('Duplizieren') === true ? (
        <Button className="button-c" disabled={selectedId === null} onClick={clone}>
          Container duplizieren
        </Button>
      ) : null}
      {permissions?.AllowDelete === true ? (
        <DeleteButton selectedId={selectedId} onClick={() => page.deleteSelected()} />
      ) : null}
      <ExcelExportButton running={page.excelExportRunning} onClick={() => page.exportExcel()} />
    </>
  );

  return (
    <>
      <MessageDialog
        open={message.open}
        onClose={message.close}
        caption={message.caption}
        text={message.text}
        width="450px"
      />
      <ContainerCloneDialog
        open={cloneDialog.visible}
        onClose={() => {
          cloneDialog.hide();
        }}
        viewId={viewId}
        containerId={selectedId ?? 0}
        onSuccess={refreshGrid}
      />
      <ContainerDocumentUploadDialog
        open={uploadDialog.visible}
        onClose={() => {
          uploadDialog.hide();
        }}
        viewId={viewId}
        containerId={selectedId ?? 0}
      />
      <ContainerDocumentListDialog
        open={listDialog.visible}
        onClose={() => {
          listDialog.hide();
        }}
        viewId={viewId}
        containerId={selectedId ?? 0}
      />

      <ViewPageFrame view={view} gridClassName="view-grid-container" toolbar={toolbar}>
        <DataGrid<Container>
          ref={page.gridRef}
          id={`grid-container-${view?.ViewName ?? ''}`}
          columns={page.columns}
          view={view}
          dataSource={page.dataSource}
          onCellSave={(item, columnName) => page.saveCell(item, columnName)}
          onSelectedIdChange={(id) => {
            page.setSelectedId(id);
          }}
        />
      </ViewPageFrame>

      {permissions?.AllowAudit === true ? (
        <ViewAuditSidebar
          entity={EntityTypes.Container}
          viewId={viewId}
          selectedId={selectedId}
          open={auditSidebar.visible}
          onClose={() => {
            auditSidebar.hide();
          }}
        />
      ) : null}
      {permissions?.AllowCreate === true ? (
        <ContainerSidebar
          open={containerSidebar.visible}
          position="Right"
          target=".view-page"
          onHide={() => {
            containerSidebar.hide();
          }}
        />
      ) : null}
    </>
  );
}

export default ContainerView;
